/**
 * Minutes of the monthly and board meetings (Maggie's 8.3): drafted by a
 * director from the notes, signed off by the director named in Admin (never
 * the person who chaired that meeting: then the deputy, or any other
 * director), then circulated, all within so many working days of the
 * meeting. Once signed they're frozen.
 */
import { addDays, type ISODate } from "./dates";
import type { Meeting } from "./meetings";
import type { PackSettings } from "./packs";
import type { Role } from "./types";

export type MinutesState = "awaiting" | "draft" | "with_signer" | "signed" | "circulated";
export const MINUTES_STATE_LABEL: Record<MinutesState, string> = { awaiting: "Awaiting notes", draft: "Draft", with_signer: "For sign-off", signed: "Signed off", circulated: "Circulated" };

export interface MinutesNotes {
  /** The notes Doc (Google Meet's, or a link pasted in). */
  url: string | null;
  fileId: string | null;
  title: string | null;
  importedAt: string | null;
}

export interface MinutesActionRow {
  ref: string;
  title: string;
  owner: string | null;
  due: ISODate | null;
}

/** As signed: what the PDF and Records show, never changed afterwards. */
export interface MinutesDocument {
  title: string;
  kind: Meeting["kind"];
  date: ISODate;
  chair: string | null;
  present: string;
  apologies: string;
  discussion: string;
  decisions: string;
  actions: MinutesActionRow[];
  signedBy: string | null;
  signedAt: string;
  version: number;
  footer: string;
}

export interface Minutes {
  meetingId: string;
  state: MinutesState;
  chairId: string | null;
  present: string;
  apologies: string;
  discussion: string;
  decisions: string;
  notes: MinutesNotes;
  savedBy: string | null;
  savedAt: string | null;
  sentBy: string | null;
  sentAt: string | null;
  /** Who it's with for sign-off (or who signed); null: any director who didn't chair it. */
  signerId: string | null;
  returnedNote: string | null;
  returnedBy: string | null;
  returnedAt: string | null;
  signedBy: string | null;
  signedAt: string | null;
  circulatedBy: string | null;
  circulatedAt: string | null;
  version: number;
  document: MinutesDocument | null;
}

/** For lists, Home and reminders: no text. */
export type MinutesSummary = Pick<Minutes, "meetingId" | "state" | "chairId" | "sentAt" | "signerId" | "signedAt" | "signedBy" | "circulatedAt" | "returnedAt"> & { savedAt: string | null };

export const emptyMinutes = (meetingId: string, chairId: string | null): Minutes => ({
  meetingId,
  state: "awaiting",
  chairId,
  present: "",
  apologies: "",
  discussion: "",
  decisions: "",
  notes: { url: null, fileId: null, title: null, importedAt: null },
  savedBy: null,
  savedAt: null,
  sentBy: null,
  sentAt: null,
  signerId: null,
  returnedNote: null,
  returnedBy: null,
  returnedAt: null,
  signedBy: null,
  signedAt: null,
  circulatedBy: null,
  circulatedAt: null,
  version: 0,
  document: null,
});

/** Meetings that are minuted: the monthly and board meetings, not cancelled. */
export const minuted = (m: Pick<Meeting, "kind" | "status">) => m.status === "scheduled" && (m.kind === "monthly" || m.kind === "board");

/** Who chairs a meeting unless the minutes say otherwise. */
export const defaultChair = (m: Pick<Meeting, "kind">, s: Pick<PackSettings, "chairId" | "monthlyChairId">) => (m.kind === "board" ? s.chairId : s.monthlyChairId);

/** Who signs off: the named director, or the deputy when the named one chaired; null when both chaired or none is set (any director who didn't chair). */
export function signerFor(chairId: string | null, s: Pick<PackSettings, "minutesById" | "minutesDeputyId">, active: (id: string) => boolean): string | null {
  for (const id of [s.minutesById, s.minutesDeputyId]) if (id && id !== chairId && active(id)) return id;
  return null;
}

export const canSignMinutes = (me: { id: string; role: Role }, m: Pick<Minutes, "state" | "signerId" | "chairId">) =>
  m.state === "with_signer" && me.role === "director" && me.id !== m.chairId && (m.signerId === null || m.signerId === me.id);

const isWorking = (d: ISODate, holidays: ReadonlySet<string>) => {
  const w = new Date(`${d}T00:00:00Z`).getUTCDay();
  return w !== 0 && w !== 6 && !holidays.has(d);
};
/** `days` working days after the meeting, bank holidays skipped. */
export function minutesDue(meetingOn: ISODate, days: number, holidays: ReadonlySet<string> = new Set()): ISODate {
  let d = meetingOn;
  for (let n = 0; n < days; ) {
    d = addDays(d, 1);
    if (isWorking(d, holidays)) n++;
  }
  return d;
}
